import React, { useMemo } from 'react';
import { Box, Button, ButtonBase, Chip, Paper, Stack, Typography } from '@mui/material';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { Booking } from '../../hooks/api/booking';
import { Lesson } from '../../hooks/api/lesson';
import {
  addDaysToDate,
  convertDateToDateString,
  convertDateToTimeString,
  formatDateToDayAndMonth,
  formatDateToWeekRange,
  formatDateToWeekday,
  getStartOfWeek,
} from '../../util/dates';

interface InstructorWeeklyScheduleProps {
  activeLessonId?: number;
  bookingsByLessonId: Map<number, Booking[]>;
  currentDate: Date;
  lessons: Lesson[];
  onLessonSelect: (lessonId: number) => void;
  onNextWeek: () => void;
  onPreviousWeek: () => void;
  onToday: () => void;
  weekStartDate: Date;
}

const formatLessonType = (type: Lesson['type']) => {
  return type === 'nogi' ? 'No-Gi' : type.toUpperCase();
};

export default function InstructorWeeklySchedule({
  activeLessonId,
  bookingsByLessonId,
  currentDate,
  lessons,
  onLessonSelect,
  onNextWeek,
  onPreviousWeek,
  onToday,
  weekStartDate,
}: InstructorWeeklyScheduleProps) {
  const lessonsByDate = useMemo(() => {
    return lessons.reduce<Record<string, Lesson[]>>((accumulator, lesson) => {
      const lessonDate = lesson.startDate.slice(0, 10);

      if (!accumulator[lessonDate]) {
        accumulator[lessonDate] = [];
      }

      accumulator[lessonDate].push(lesson);
      return accumulator;
    }, {});
  }, [lessons]);

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, index) => {
      const currentDate = addDaysToDate(weekStartDate, index);
      const dateKey = convertDateToDateString(currentDate);

      return {
        date: currentDate,
        dateKey,
        lessons: lessonsByDate[dateKey] ?? [],
      };
    });
  }, [lessonsByDate, weekStartDate]);

  const today = convertDateToDateString(new Date());
  const isCurrentWeek =
    getStartOfWeek(currentDate).getTime() === getStartOfWeek(new Date()).getTime();

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 2 },
        mb: 2,
        borderRadius: 4,
        border: '1px solid rgba(18,18,18,0.08)',
        backgroundColor: 'white',
      }}
    >
      <Stack spacing={1.5}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.25}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111111' }}>
            {`Semana del ${formatDateToWeekRange(weekStartDate)}`}
          </Typography>

          <Stack direction="row" spacing={0.75}>
            <Button
              variant="outlined"
              size="small"
              onClick={onPreviousWeek}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                minWidth: 36,
                px: 1,
                borderColor: 'rgba(18,18,18,0.12)',
                color: '#111111',
              }}
              aria-label="Semana anterior"
            >
              <ChevronLeftRoundedIcon fontSize="small" />
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={onToday}
              disabled={isCurrentWeek}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                borderColor: 'rgba(18,18,18,0.12)',
                color: '#111111',
                fontWeight: 600,
              }}
            >
              Hoy
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={onNextWeek}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                minWidth: 36,
                px: 1,
                borderColor: 'rgba(18,18,18,0.12)',
                color: '#111111',
              }}
              aria-label="Semana siguiente"
            >
              <ChevronRightRoundedIcon fontSize="small" />
            </Button>
          </Stack>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(160px, 1fr))',
            gap: 1.5,
            overflowX: 'auto',
            pb: 0.5,
          }}
        >
          {weekDays.map((day) => {
            const isToday = day.dateKey === today;

            return (
              <Box
                key={day.dateKey}
                sx={{
                  borderRadius: 4,
                  minWidth: 160,
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    minHeight: 240,
                    p: 1.5,
                    borderRadius: 4,
                    border: '1px solid rgba(18,18,18,0.08)',
                    borderTop: isToday ? '2px solid #111111' : '1px solid rgba(18,18,18,0.08)',
                    backgroundColor: isToday ? '#f3f3f3' : 'white',
                    color: '#111111',
                  }}
                >
                  <Stack spacing={1.5} sx={{ height: '100%' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: 'rgba(17,17,17,0.56)',
                          }}
                        >
                          {formatDateToWeekday(day.date)}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {formatDateToDayAndMonth(day.date)}
                        </Typography>
                      </Box>

                      {isToday && (
                        <Chip
                          label="Hoy"
                          size="small"
                          sx={{
                            bgcolor: '#111111',
                            color: 'white',
                            fontWeight: 700,
                          }}
                        />
                      )}
                    </Stack>

                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(17,17,17,0.56)',
                      }}
                    >
                      {day.lessons.length === 0
                        ? 'Sin clases programadas'
                        : `${day.lessons.length} ${day.lessons.length === 1 ? 'clase' : 'clases'}`}
                    </Typography>

                    <Stack spacing={1} sx={{ flex: 1 }}>
                      {day.lessons.length === 0 ? (
                        <Box
                          sx={{
                            borderRadius: 3,
                            border: '1px dashed rgba(17,17,17,0.12)',
                            px: 1.25,
                            py: 2,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'rgba(17,17,17,0.48)',
                            }}
                          >
                            Día libre para nuevas clases o ajustes de agenda.
                          </Typography>
                        </Box>
                      ) : (
                        day.lessons.map((lesson) => {
                          const bookings = bookingsByLessonId.get(lesson.id) ?? [];
                          const isActiveLesson = lesson.id === activeLessonId;

                          return (
                            <ButtonBase
                              key={lesson.id}
                              onClick={() => onLessonSelect(lesson.id)}
                              sx={{
                                borderRadius: 3,
                                display: 'block',
                                textAlign: 'left',
                                width: '100%',
                              }}
                            >
                              <Box
                                sx={{
                                  p: 1.25,
                                  borderRadius: 3,
                                  bgcolor: isActiveLesson ? '#111111' : '#f7f7f7',
                                  border: isActiveLesson
                                    ? '1px solid rgba(17,17,17,0.9)'
                                    : '1px solid rgba(18,18,18,0.08)',
                                  color: isActiveLesson ? 'white' : '#111111',
                                  transition:
                                    'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
                                  '&:hover': {
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 10px 18px rgba(17,17,17,0.08)',
                                  },
                                }}
                              >
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {`${convertDateToTimeString(
                                    new Date(lesson.startDate),
                                  )} - ${convertDateToTimeString(new Date(lesson.endDate))}`}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: isActiveLesson
                                      ? 'rgba(255,255,255,0.72)'
                                      : 'rgba(17,17,17,0.72)',
                                  }}
                                >
                                  {formatLessonType(lesson.type)}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    mt: 0.5,
                                    color: isActiveLesson
                                      ? 'rgba(255,255,255,0.56)'
                                      : 'rgba(17,17,17,0.56)',
                                  }}
                                >
                                  {`${lesson.professor.firstName} ${lesson.professor.lastName}`}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    mt: 1,
                                    fontWeight: 700,
                                    color: isActiveLesson
                                      ? 'rgba(255,255,255,0.72)'
                                      : 'rgba(17,17,17,0.58)',
                                  }}
                                >
                                  {`${bookings.length}/${lesson.maxSeats} reservas`}
                                </Typography>
                              </Box>
                            </ButtonBase>
                          );
                        })
                      )}
                    </Stack>
                  </Stack>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Stack>
    </Paper>
  );
}
